import { FolderTree, Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { EmptyState, ErrorState, LoadingState } from "@/components/feedback";
import { workspace } from "@/components/workspace/controller";
import { Button } from "@/components/ui/button";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { CATEGORY_LABEL, NOTE_CATEGORIES } from "@/lib/api";
import { cn } from "@/lib/utils";

import { FilterPopover } from "./FilterPopover";
import { NoteCard } from "./NoteCard";
import { useHome } from "./home-store";
import { flattenFeed, groupByDay, useNotesFeed } from "./useNotesFeed";

/**
 * The card grid's breakpoints. Driven by the PANEL's width, not the viewport's:
 * Home lives inside a dockview panel that can be floating, snapped to half the
 * screen, or maximized, so a viewport media query would report a width this grid
 * does not have. Measured with a ResizeObserver instead.
 *
 * Three across when there's room, two when there isn't, one when there really
 * isn't — the brief's requirement, expressed against the right box.
 */
const THREE_COLUMN_MIN = 900;
const TWO_COLUMN_MIN = 560;

function useColumns(ref: React.RefObject<HTMLElement>): 1 | 2 | 3 {
  const [columns, setColumns] = useState<1 | 2 | 3>(3);
  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry?.contentRect.width ?? 0;
      setColumns(width >= THREE_COLUMN_MIN ? 3 : width >= TWO_COLUMN_MIN ? 2 : 1);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return columns;
}

const GRID_CLASS: Record<1 | 2 | 3, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
};

/** The one line that keeps L1's hidden filters from becoming invisible
 * modality: what is narrowing the list, and one click to undo it. Renders only
 * when something is actually active, so the calm default page stays calm. */
function FilterSummary() {
  const categories = useHome((s) => s.categories);
  const folder = useHome((s) => s.folder);
  const folderCategory = useHome((s) => s.folderCategory);
  const sort = useHome((s) => s.sort);
  const active = useHome((s) => s.activeFilterCount());
  if (active === 0) return null;

  const parts: string[] = [];
  // A folder path already names its category ("Markdown / Research"), so listing
  // the category separately would print it twice on the same line.
  const categoryImpliedByFolder =
    !!folder && categories.length === 1 && categories[0] === folderCategory;
  if (
    !categoryImpliedByFolder &&
    categories.length > 0 &&
    categories.length < NOTE_CATEGORIES.length
  ) {
    parts.push(categories.map((c) => CATEGORY_LABEL[c]).join(", "));
  }
  if (folder) parts.push(`${folderCategory ? `${CATEGORY_LABEL[folderCategory]} / ` : ""}${folder}`);
  if (sort === "alpha") parts.push("A–Z");

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-2 text-[11.5px] text-muted-foreground">
      <span className="font-mono text-[9px] uppercase tracking-widest">Showing</span>
      {parts.map((part, i) => (
        <span key={part} className="flex items-center gap-2">
          {i > 0 && <span className="opacity-40">·</span>}
          <span className="text-foreground/85">{part}</span>
        </span>
      ))}
      <button
        type="button"
        onClick={() => useHome.getState().clearFilters()}
        className="ml-1 flex items-center gap-1 rounded-full border border-border px-2 py-px text-[10px] transition-colors hover:border-primary/50 hover:text-foreground"
      >
        clear <X className="h-2.5 w-2.5" />
      </button>
    </div>
  );
}

/**
 * Home — "All Notes".
 *
 * Layout L1 "Ledger": heading, search directly beneath it, Filter and Folder
 * View beside it, then reverse-chronological date groups of cards. Nothing
 * competes with the search box and nothing takes width from the cards.
 */
export function HomeTab() {
  const query = useHome((s) => s.query);
  const sort = useHome((s) => s.sort);
  const categories = useHome((s) => s.categories);
  const folder = useHome((s) => s.folder);
  // One request per settled query, not one per keystroke — the same 250ms the
  // inspector's search box uses.
  const debouncedQuery = useDebouncedValue(query, 250);
  const searching = debouncedQuery.trim().length > 0;

  const gridRef = useRef<HTMLDivElement>(null);
  const columns = useColumns(gridRef);
  const feed = useNotesFeed({ query: debouncedQuery, sort, categories, folder });

  const cards = useMemo(() => flattenFeed(feed.data?.pages), [feed.data]);
  // Date headings only make sense for a chronological list. Under an
  // alphabetical or relevance order they would jump back and forth, so those
  // render flat.
  const grouped = sort === "recent" && !searching;
  const groups = useMemo(() => (grouped ? groupByDay(cards) : []), [grouped, cards]);

  const onSelectFolder = (path: string) => {
    const card = cards.find((c) => (c.folder_path ?? []).join("/").startsWith(path));
    useHome.getState().setFolder(card?.category ?? null, path);
  };

  const grid = (list: typeof cards) => (
    <div className={cn("grid gap-3", GRID_CLASS[columns])}>
      {list.map((card) => (
        <NoteCard key={`${card.kind}:${card.id}`} card={card} onSelectFolder={onSelectFolder} />
      ))}
    </div>
  );

  return (
    <div className="h-full w-full overflow-y-auto bg-background">
      <div ref={gridRef} className="mx-auto w-full max-w-[1180px] px-6 pb-16 pt-8">
        <h1 className="font-display text-[27px] font-medium leading-tight tracking-[-0.01em] text-foreground">
          All Notes
        </h1>

        {/* Wraps on a phone. Three controls on one line left the search field
            about 120px wide with its own placeholder cut off mid-word; the
            field now owns the first line and the two buttons drop below it,
            which is also the order they are used in. */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="relative flex min-w-[min(100%,220px)] flex-1 items-center">
            <Search className="pointer-events-none absolute left-3 h-3.5 w-3.5 text-muted-foreground" />
            <input
              type="search"
              value={query}
              onChange={(e) => useHome.getState().setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape" && query) useHome.getState().setQuery("");
              }}
              placeholder="Search all notes…"
              aria-label="Search all notes"
              className="h-9 w-full rounded-md border border-border bg-input pl-9 pr-3 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <FilterPopover />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => workspace.openFiles()}
            title="Browse and organise files in a full explorer"
            className="max-md:h-10"
          >
            <FolderTree className="h-3.5 w-3.5" />
            Folder view
          </Button>
        </div>
        <FilterSummary />

        <div className="mt-5">
          {feed.isLoading && <LoadingState variant="skeleton" rows={6} />}
          {feed.isError && (
            <ErrorState error={feed.error} onRetry={() => void feed.refetch()} label="notes" />
          )}
          {!feed.isLoading && !feed.isError && cards.length === 0 && (
            <EmptyState
              icon={Search}
              title={searching ? "No notes match that" : "Nothing here yet"}
              description={
                searching
                  ? "Try fewer words, or clear the filters."
                  : "Upload a document, write a note, or capture a website — everything you make lands here."
              }
              className="pt-14"
            />
          )}

          {grouped
            ? groups.map((group) => (
                <section key={group.key} className="mb-7">
                  <div className="mb-2.5 flex items-center gap-3">
                    <h2 className="font-display text-[14.5px] font-medium text-foreground">
                      {group.label}
                    </h2>
                    <span className="font-mono text-[10px] text-muted-foreground">
                      {group.cards.length}
                    </span>
                    <span className="h-px flex-1 bg-border" />
                  </div>
                  {grid(group.cards)}
                </section>
              ))
            : cards.length > 0 && grid(cards)}

          {feed.hasNextPage && (
            <div className="flex justify-center pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={feed.isFetchingNextPage}
                onClick={() => void feed.fetchNextPage()}
              >
                {feed.isFetchingNextPage ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
